import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SupplierLayout } from '../../components/SupplierLayout';
import { useSupplierAuth } from '../../lib/supplierAuthStore';
import { useSupplierStore } from '../../lib/supplierStore';
import { toast } from 'sonner';
import { Building2, Mail, Phone, MapPin, FileText, CheckCircle2, Sparkles, Truck, Plus, X, Send, Wand2, Route, Tag, MapPinned, MoreVertical, Trash2, Edit2, AlertCircle, ChevronDown, ArrowRight, Shield, Users, Lock, UserPlus, Eye, EyeOff, Settings2, Zap, Search, Check } from 'lucide-react';
type ChatMsg = {
  id: string;
  from: 'ai' | 'user';
  text: string;
  suggestions?: Suggestion[];
};
const UAE_CITIES = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain', 'Al Ain'];
// Mapping from truck type keywords to suggested services.
type Suggestion = {
  service: string;
  rationale: string;
  basedOn: string[];
};
function suggestServicesFromTrucks(truckTypes: string[]): Suggestion[] {
  const set = (s: string) => s.toLowerCase();
  const types = truckTypes.map(set);
  const suggestions: Suggestion[] = [];
  const has = (kw: string) => types.some(t => t.includes(kw));
  const matching = (kw: string) => truckTypes.filter(t => t.toLowerCase().includes(kw));
  if (has('recovery')) {
    suggestions.push({
      service: 'Flat Tire Repair',
      rationale: 'Your recovery crew can carry a portable inflator and patch kit — high-margin roadside add-on.',
      basedOn: matching('recovery')
    });
    suggestions.push({
      service: 'Jump Start',
      rationale: 'Quick add-on for recovery trucks — bundles naturally with Battery Boost.',
      basedOn: matching('recovery')
    });
    suggestions.push({
      service: 'Fuel Delivery',
      rationale: 'Many breakdowns are just empty tanks. A small fuel canister on recovery runs is pure margin.',
      basedOn: matching('recovery')
    });
    suggestions.push({
      service: 'Lockout Assistance',
      rationale: 'Pair with a certified locksmith partner to capture roadside lockout calls.',
      basedOn: matching('recovery')
    });
    suggestions.push({
      service: 'Car Movement',
      rationale: 'Recovery flatbeds double up nicely for showroom and dealer transport between cities.',
      basedOn: matching('recovery')
    });
  }
  if (has('box') || has('curtain')) {
    suggestions.push({
      service: 'Office Move',
      rationale: 'Box and curtain-side trucks handle desks, monitors and IT crates comfortably.',
      basedOn: [...matching('box'), ...matching('curtain')]
    });
    suggestions.push({
      service: 'Storage Pickup & Delivery',
      rationale: 'Reposition customer belongings from self-storage units — recurring monthly demand.',
      basedOn: [...matching('box'), ...matching('curtain')]
    });
  }
  if (has('pickup')) {
    suggestions.push({
      service: 'Last-mile Delivery',
      rationale: 'Pickups are agile for B2B parcel and small load delivery.',
      basedOn: matching('pickup')
    });
    suggestions.push({
      service: 'Appliance Delivery & Install',
      rationale: 'Fridges, washers, ACs — bundle delivery with install for higher ticket size.',
      basedOn: matching('pickup')
    });
  }
  if (has('flatbed') && !has('recovery')) {
    suggestions.push({
      service: 'B2B Pallet Delivery',
      rationale: 'Flatbeds handle palletised cargo and long loads efficiently.',
      basedOn: matching('flatbed').filter(t => !t.toLowerCase().includes('recovery'))
    });
  }
  if (has('low-bed') || has('40-ton') || has('flatbed 12')) {
    suggestions.push({
      service: 'Heavy Equipment Transport',
      rationale: 'Your low-bed and 12m flatbed can carry excavators, generators and oversized loads.',
      basedOn: [...matching('low-bed'), ...matching('flatbed 12')]
    });
    suggestions.push({
      service: 'Construction Site Logistics',
      rationale: 'Recurring contracts moving equipment and materials between active sites.',
      basedOn: [...matching('low-bed'), ...matching('flatbed 12')]
    });
  }
  if (has('refrigerat') || has('reefer') || has('chill')) {
    suggestions.push({
      service: 'Refrigerated Transport',
      rationale: 'Cold-chain capability — perishables, pharmacy, and grocery runs.',
      basedOn: [...matching('refrigerat'), ...matching('reefer')]
    });
  }
  return suggestions;
}
export function SupplierProfile() {
  const {
    user
  } = useSupplierAuth();
  const {
    trucks,
    services,
    addService,
    removeService,
    pricingGroups,
    serviceGroupMap,
    addPricingGroup,
    updatePricingGroup,
    deletePricingGroup,
    assignServiceGroup,
    autoDispatch,
    updateAutoDispatch
  } = useSupplierStore();
  const truckTypes = useMemo(() => Array.from(new Set(trucks.map(t => t.type))), [trucks]);
  const allSuggestions = useMemo(() => suggestServicesFromTrucks(truckTypes), [truckTypes]);
  // Suggestions not yet added to services
  const pendingSuggestions = useMemo(() => allSuggestions.filter(s => !services.some(sv => sv.toLowerCase() === s.service.toLowerCase())), [allSuggestions, services]);
  const [activeTab, setActiveTab] = useState<'business' | 'services' | 'pricing' | 'autodispatch' | 'users' | 'security'>('business');
  const TABS = [{
    id: 'business',
    label: 'Business Details',
    icon: Building2
  }, {
    id: 'services',
    label: 'AI Service Advisor',
    icon: Sparkles
  }, {
    id: 'pricing',
    label: 'Service Pricing',
    icon: Tag
  }, {
    id: 'autodispatch',
    label: 'Auto Dispatch',
    icon: Zap
  }, {
    id: 'users',
    label: 'Users & Roles',
    icon: Users
  }, {
    id: 'security',
    label: 'Security',
    icon: Shield
  }] as const;
  return <SupplierLayout data-id="element-3537">
      <div className="max-w-6xl mx-auto" data-id="element-3538">
        <h1 className="text-2xl font-display font-bold text-gray-900 mb-6" data-id="element-3539">
          Profile & Settings
        </h1>

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-3540">
          {/* Sidebar Navigation */}
          <div className="w-full lg:w-64 shrink-0" data-id="element-3541">
            {/* Mobile/Tablet Horizontal Scroll */}
            <div className="flex lg:hidden overflow-x-auto pb-4 gap-2 scrollbar-hide" data-id="element-3542">
              {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-colors ${isActive ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`} data-id="element-3543">
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-400'}`} data-id="element-3544" />
                    {tab.label}
                  </button>;
            })}
            </div>

            {/* Desktop Vertical Sidebar */}
            <div className="hidden lg:flex flex-col gap-1 sticky top-24" data-id="element-3545">
              {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${isActive ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:bg-white hover:text-gray-900'}`} data-id="element-3546">
                    <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-400'}`} data-id="element-3547" />
                    {tab.label}
                  </button>;
            })}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0" data-id="element-3548">
            <AnimatePresence mode="wait" data-id="element-3549">
              <motion.div key={activeTab} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-3550">
                {activeTab === 'business' && <BusinessTab data-id="element-3551" />}
                {activeTab === 'services' && <ServicesTab data-id="element-3552" />}
                {activeTab === 'pricing' && <PricingTab data-id="element-3553" />}
                {activeTab === 'autodispatch' && <AutoDispatchTab data-id="element-3554" />}
                {activeTab === 'users' && <UsersTab data-id="element-3555" />}
                {activeTab === 'security' && <SecurityTab data-id="element-3556" />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </SupplierLayout>;
  function AutoDispatchTab() {
    const [selectedTruckType, setSelectedTruckType] = useState<string | null>(truckTypes[0] || null);
    const [serviceSearch, setServiceSearch] = useState('');
    const handleToggleService = (truckType: string, service: string) => {
      const currentMap = autoDispatch.truckServiceMap || {};
      const currentServices = currentMap[truckType] || [];
      const isSelected = currentServices.includes(service);
      const newServices = isSelected ? currentServices.filter(s => s !== service) : [...currentServices, service];
      updateAutoDispatch({
        truckServiceMap: {
          ...currentMap,
          [truckType]: newServices
        }
      });
    };
    const handleBulkSet = (truckType: string, all: boolean) => {
      const currentMap = autoDispatch.truckServiceMap || {};
      updateAutoDispatch({
        truckServiceMap: {
          ...currentMap,
          [truckType]: all ? [...services] : []
        }
      });
    };
    const filteredServices = services.filter(s => s.toLowerCase().includes(serviceSearch.toLowerCase()));
    const selectedEnabled = selectedTruckType ? autoDispatch.truckServiceMap?.[selectedTruckType] || [] : [];
    return <div className="space-y-6" data-id="element-3557">
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-id="element-3558">
          <div className="flex items-start justify-between mb-6" data-id="element-3559">
            <div data-id="element-3560">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-3561">
                <Zap className="w-5 h-5 text-blue-600" data-id="element-3562" />
                Auto Dispatch Engine
              </h2>
              <p className="text-sm text-gray-500 mt-1" data-id="element-3563">
                Automatically assign incoming jobs to the best available driver
                based on your custom rules.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-3564">
              <input type="checkbox" className="sr-only peer" checked={autoDispatch.enabled} onChange={e => updateAutoDispatch({
              enabled: e.target.checked
            })} data-id="element-3565" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" data-id="element-3566"></div>
            </label>
          </div>

          <div className={`space-y-8 transition-opacity duration-200 ${!autoDispatch.enabled ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-3567">
            {/* Algorithm Weights */}
            <div data-id="element-3568">
              <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-3569">
                <Settings2 className="w-4 h-4 text-gray-500" data-id="element-3570" />
                Algorithm Weights
              </h3>
              <div className="space-y-6" data-id="element-3571">
                <div data-id="element-3572">
                  <div className="flex justify-between text-sm mb-2" data-id="element-3573">
                    <span className="font-medium text-gray-700" data-id="element-3574">
                      Distance (Nearest Driver)
                    </span>
                    <span className="text-gray-500" data-id="element-3575">
                      {autoDispatch.weightDistance}%
                    </span>
                  </div>
                  <input type="range" min="0" max="100" value={autoDispatch.weightDistance} onChange={e => updateAutoDispatch({
                  weightDistance: parseInt(e.target.value)
                })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" data-id="element-3576" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-3577">
                    Prioritize drivers closest to the pickup location.
                  </p>
                </div>

                <div data-id="element-3578">
                  <div className="flex justify-between text-sm mb-2" data-id="element-3579">
                    <span className="font-medium text-gray-700" data-id="element-3580">
                      Workload (Fewer Active Jobs)
                    </span>
                    <span className="text-gray-500" data-id="element-3581">
                      {autoDispatch.weightWorkload}%
                    </span>
                  </div>
                  <input type="range" min="0" max="100" value={autoDispatch.weightWorkload} onChange={e => updateAutoDispatch({
                  weightWorkload: parseInt(e.target.value)
                })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" data-id="element-3582" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-3583">
                    Prioritize drivers with lighter schedules to balance
                    workload.
                  </p>
                </div>

                <div data-id="element-3584">
                  <div className="flex justify-between text-sm mb-2" data-id="element-3585">
                    <span className="font-medium text-gray-700" data-id="element-3586">
                      Driver Rating
                    </span>
                    <span className="text-gray-500" data-id="element-3587">
                      {autoDispatch.weightRating}%
                    </span>
                  </div>
                  <input type="range" min="0" max="100" value={autoDispatch.weightRating} onChange={e => updateAutoDispatch({
                  weightRating: parseInt(e.target.value)
                })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" data-id="element-3588" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-3589">
                    Prioritize drivers with higher customer ratings.
                  </p>
                </div>
              </div>
            </div>

            <hr className="border-gray-100" data-id="element-3590" />

            {/* Service Capabilities */}
            <div data-id="element-3591">
              <h3 className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-2" data-id="element-3592">
                <Truck className="w-4 h-4 text-gray-500" data-id="element-3593" />
                Truck Capabilities
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-3594">
                Pick a truck type, then choose which services it can perform.
                The engine only auto-dispatches jobs to capable trucks.
              </p>

              {truckTypes.length === 0 ? <div className="text-sm text-gray-500 text-center py-8 border-2 border-dashed border-gray-200 rounded-xl" data-id="element-3595">
                  No trucks available. Add trucks in the Trucks module first.
                </div> : <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 border border-gray-200 rounded-xl overflow-hidden" data-id="element-3596">
                  {/* Left: Truck type list */}
                  <div className="lg:border-r border-gray-200 lg:max-h-[460px] lg:overflow-y-auto bg-gray-50/50" data-id="element-3597">
                    <div className="lg:hidden p-3 border-b border-gray-200 bg-white" data-id="element-3598">
                      <select value={selectedTruckType || ''} onChange={e => setSelectedTruckType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-gray-900 focus:border-gray-900" data-id="element-3599">
                        {truckTypes.map(type => <option key={type} value={type} data-id="element-3600">
                            {type} (
                            {(autoDispatch.truckServiceMap?.[type] || []).length}
                            /{services.length})
                          </option>)}
                      </select>
                    </div>
                    <div className="hidden lg:block p-2 space-y-1" data-id="element-3601">
                      {truckTypes.map(type => {
                    const enabledCount = (autoDispatch.truckServiceMap?.[type] || []).length;
                    const isActive = selectedTruckType === type;
                    const isAll = enabledCount === services.length;
                    const isNone = enabledCount === 0;
                    return <button key={type} onClick={() => setSelectedTruckType(type)} className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center justify-between gap-2 ${isActive ? 'bg-white shadow-sm border border-gray-200' : 'hover:bg-white/70 border border-transparent'}`} data-id="element-3602">
                            <div className="min-w-0 flex-1" data-id="element-3603">
                              <div className="text-sm font-semibold text-gray-900 truncate" data-id="element-3604">
                                {type}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5" data-id="element-3605">
                                {enabledCount} of {services.length} services
                              </div>
                            </div>
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${isAll ? 'bg-emerald-100 text-emerald-700' : isNone ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-700'}`} data-id="element-3606">
                              {isAll ? 'All' : isNone ? 'None' : 'Partial'}
                            </span>
                          </button>;
                  })}
                    </div>
                  </div>

                  {/* Right: Services panel */}
                  {selectedTruckType && <div className="bg-white lg:max-h-[460px] flex flex-col" data-id="element-3607">
                      <div className="p-4 border-b border-gray-100" data-id="element-3608">
                        <div className="flex items-start justify-between gap-3 mb-3" data-id="element-3609">
                          <div data-id="element-3610">
                            <div className="text-sm font-bold text-gray-900" data-id="element-3611">
                              {selectedTruckType}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-3612">
                              {selectedEnabled.length} of {services.length}{' '}
                              services enabled
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0" data-id="element-3613">
                            <button onClick={() => handleBulkSet(selectedTruckType, true)} className="text-xs font-medium text-gray-700 hover:text-gray-900 px-2.5 py-1 rounded-md hover:bg-gray-100 transition-colors" data-id="element-3614">
                              Select all
                            </button>
                            <button onClick={() => handleBulkSet(selectedTruckType, false)} className="text-xs font-medium text-gray-700 hover:text-gray-900 px-2.5 py-1 rounded-md hover:bg-gray-100 transition-colors" data-id="element-3615">
                              Clear
                            </button>
                          </div>
                        </div>
                        <div className="relative" data-id="element-3616">
                          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-3617" />
                          <input type="text" value={serviceSearch} onChange={e => setServiceSearch(e.target.value)} placeholder="Search services..." className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-gray-900 focus:border-gray-900" data-id="element-3618" />
                        </div>
                      </div>

                      <div className="flex-1 overflow-y-auto p-4" data-id="element-3619">
                        {filteredServices.length === 0 ? <div className="text-sm text-gray-500 text-center py-8" data-id="element-3620">
                            No services match "{serviceSearch}"
                          </div> : <div className="flex flex-wrap gap-2" data-id="element-3621">
                            {filteredServices.map(service => {
                      const isSelected = selectedEnabled.includes(service);
                      return <button key={service} onClick={() => handleToggleService(selectedTruckType, service)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${isSelected ? 'bg-gray-900 text-white border-gray-900 hover:bg-gray-800' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`} data-id="element-3622">
                                  {isSelected && <Check className="w-3.5 h-3.5" data-id="element-3623" />}
                                  {service}
                                </button>;
                    })}
                          </div>}
                      </div>
                    </div>}
                </div>}
            </div>
          </div>
        </div>
      </div>;
  }
  function BusinessTab() {
    const [isTradeLicenseModalOpen, setIsTradeLicenseModalOpen] = useState(false);
    return <div className="space-y-6" data-id="element-3624">
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-id="element-3625">
          <div className="mb-6" data-id="element-3626">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-3627">
              Business Details
            </h2>
            <p className="text-sm text-gray-500" data-id="element-3628">
              Information shown to customers and used on invoices
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3629">
            <div data-id="element-3630">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3631">
                Business Name
              </label>
              <div className="relative" data-id="element-3632">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3633">
                  <Building2 className="h-4 w-4 text-gray-400" data-id="element-3634" />
                </div>
                <input type="text" defaultValue={user?.businessName || 'Falcon Logistics LLC'} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3635" />
              </div>
            </div>

            <div data-id="element-3636">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3637">
                Email Address
              </label>
              <div className="relative" data-id="element-3638">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3639">
                  <Mail className="h-4 w-4 text-gray-400" data-id="element-3640" />
                </div>
                <input type="email" defaultValue={user?.email || 'contact@falconlogistics.ae'} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3641" />
              </div>
            </div>

            <div data-id="element-3642">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3643">
                Phone Number
              </label>
              <div className="relative" data-id="element-3644">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3645">
                  <Phone className="h-4 w-4 text-gray-400" data-id="element-3646" />
                </div>
                <input type="tel" defaultValue="+971 50 123 4567" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3647" />
              </div>
            </div>

            <div data-id="element-3648">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3649">
                Trade License Number
              </label>
              <div className="relative" data-id="element-3650">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3651">
                  <FileText className="h-4 w-4 text-gray-400" data-id="element-3652" />
                </div>
                <input type="text" defaultValue="CN-1234567" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3653" />
              </div>
            </div>

            <div className="md:col-span-2" data-id="element-3654">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3655">
                Trade License Expiry
              </label>
              <div className="flex items-center gap-4" data-id="element-3656">
                <input type="date" defaultValue="2025-12-31" className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3657" />
                <button onClick={() => setIsTradeLicenseModalOpen(true)} className="text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-3658">
                  Update License
                </button>
              </div>
            </div>

            <div className="md:col-span-2" data-id="element-3659">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3660">
                Business Address
              </label>
              <div className="relative" data-id="element-3661">
                <div className="absolute top-3 left-3 pointer-events-none" data-id="element-3662">
                  <MapPin className="h-4 w-4 text-gray-400" data-id="element-3663" />
                </div>
                <textarea rows={3} defaultValue="Office 1502, Boulevard Plaza Tower 2, Downtown Dubai" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3664" />
              </div>
            </div>
          </div>

          <div className="mt-8 border-t border-gray-100 pt-6" data-id="element-3665">
            <h3 className="text-sm font-medium text-gray-900 mb-4" data-id="element-3666">
              Verification Status
            </h3>
            <div className="flex flex-wrap gap-4" data-id="element-3667">
              <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-3668">
                <CheckCircle2 className="w-4 h-4" data-id="element-3669" />
                Trade License Verified
              </div>
              <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-3670">
                <CheckCircle2 className="w-4 h-4" data-id="element-3671" />
                VAT Registered
              </div>
              <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-3672">
                <CheckCircle2 className="w-4 h-4" data-id="element-3673" />
                RTA Approved
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end" data-id="element-3674">
            <button onClick={() => toast.success('Business details saved')} className="bg-gray-900 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-gray-800 transition-colors" data-id="element-3675">
              Save Changes
            </button>
          </div>
        </div>

        <AnimatePresence data-id="element-3676">
          {isTradeLicenseModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-3677">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-3678">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50" data-id="element-3679">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-3680">
                    Update Trade License
                  </h3>
                  <button onClick={() => setIsTradeLicenseModalOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-3681">
                    <X className="w-5 h-5" data-id="element-3682" />
                  </button>
                </div>
                <div className="p-6 space-y-6" data-id="element-3683">
                  <div data-id="element-3684">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3685">
                      Upload Document
                    </label>
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-3686">
                      <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" data-id="element-3687" />
                      <p className="text-sm font-medium text-gray-900" data-id="element-3688">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-3689">
                        PDF or JPG, max 10MB
                      </p>
                    </div>
                  </div>
                  <div data-id="element-3690">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3691">
                      New Expiry Date
                    </label>
                    <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3692" />
                  </div>
                </div>
                <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3" data-id="element-3693">
                  <button onClick={() => setIsTradeLicenseModalOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-3694">
                    Cancel
                  </button>
                  <button onClick={() => {
                toast.success('Trade license updated');
                setIsTradeLicenseModalOpen(false);
              }} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl transition-colors" data-id="element-3695">
                    Save License
                  </button>
                </div>
              </motion.div>
            </div>}
        </AnimatePresence>
      </div>;
  }
  function ServicesTab() {
    const [newService, setNewService] = useState('');
    const [chatOpen, setChatOpen] = useState(false);
    const [chatInput, setChatInput] = useState('');
    const [chatMsgs, setChatMsgs] = useState<ChatMsg[]>([{
      id: '1',
      from: 'ai',
      text: "Hi! I'm your AI Service Advisor. Based on your fleet, I've suggested some new services you could offer. Ask me anything about expanding your business!"
    }]);
    const chatEndRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      chatEndRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }, [chatMsgs]);
    const handleSendChat = () => {
      if (!chatInput.trim()) return;
      const userMsg: ChatMsg = {
        id: Date.now().toString(),
        from: 'user',
        text: chatInput
      };
      setChatMsgs(prev => [...prev, userMsg]);
      setChatInput('');
      setTimeout(() => {
        const aiMsg: ChatMsg = {
          id: (Date.now() + 1).toString(),
          from: 'ai',
          text: "That's a great idea. Given your current truck types, expanding into that area would require adding at least one refrigerated truck to your fleet. Would you like me to analyze the potential ROI?"
        };
        setChatMsgs(prev => [...prev, aiMsg]);
      }, 1000);
    };
    return <div className="space-y-6" data-id="element-3696">
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-id="element-3697">
          <div className="mb-6 flex items-start justify-between gap-4" data-id="element-3698">
            <div data-id="element-3699">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-3700">
                <Sparkles className="w-5 h-5 text-blue-600" data-id="element-3701" />
                AI Service Advisor
              </h2>
              <p className="text-sm text-gray-500" data-id="element-3702">
                Discover new services your fleet can offer based on your truck
                types.
              </p>
            </div>
            <button onClick={() => setChatOpen(true)} className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-sm hover:shadow-md transition-all" data-id="element-3703">
              <Wand2 className="w-4 h-4" data-id="element-3704" />
              Consult with AI
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-3705">
            {/* Left: Current Services */}
            <div data-id="element-3706">
              <h3 className="text-sm font-bold text-gray-900 mb-4" data-id="element-3707">
                Your Services
              </h3>
              <div className="flex gap-2 mb-4" data-id="element-3708">
                <input type="text" value={newService} onChange={e => setNewService(e.target.value)} placeholder="Add a service manually..." className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-gray-900 focus:border-gray-900" onKeyDown={e => {
                if (e.key === 'Enter' && newService.trim()) {
                  addService(newService);
                  setNewService('');
                }
              }} data-id="element-3709" />
                <button onClick={() => {
                if (newService.trim()) {
                  addService(newService);
                  setNewService('');
                }
              }} className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800" data-id="element-3710">
                  Add
                </button>
              </div>
              <div className="space-y-2" data-id="element-3711">
                {services.map(service => <div key={service} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100" data-id="element-3712">
                    <span className="text-sm font-medium text-gray-900" data-id="element-3713">
                      {service}
                    </span>
                    <button onClick={() => removeService(service)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-3714">
                      <X className="w-4 h-4" data-id="element-3715" />
                    </button>
                  </div>)}
                {services.length === 0 && <p className="text-sm text-gray-500 text-center py-4" data-id="element-3716">
                    No services added yet.
                  </p>}
              </div>
            </div>

            {/* Right: AI Suggestions */}
            <div data-id="element-3717">
              <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-3718">
                <Wand2 className="w-4 h-4 text-purple-500" data-id="element-3719" />
                AI Suggestions
              </h3>
              <div className="space-y-3" data-id="element-3720">
                {pendingSuggestions.length > 0 ? pendingSuggestions.map((sugg, idx) => <div key={idx} className="p-4 bg-purple-50/50 border border-purple-100 rounded-xl" data-id="element-3721">
                      <div className="flex items-start justify-between gap-4" data-id="element-3722">
                        <div data-id="element-3723">
                          <h4 className="text-sm font-bold text-gray-900" data-id="element-3724">
                            {sugg.service}
                          </h4>
                          <p className="text-xs text-gray-600 mt-1" data-id="element-3725">
                            {sugg.rationale}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-2" data-id="element-3726">
                            {sugg.basedOn.map((t, i) => <span key={i} className="px-2 py-0.5 bg-white border border-purple-200 text-purple-700 rounded-md text-[10px] font-medium" data-id="element-3727">
                                {t}
                              </span>)}
                          </div>
                        </div>
                        <button onClick={() => addService(sugg.service)} className="shrink-0 p-1.5 bg-white border border-purple-200 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors" title="Add Service" data-id="element-3728">
                          <Plus className="w-4 h-4" data-id="element-3729" />
                        </button>
                      </div>
                    </div>) : <div className="p-6 text-center bg-gray-50 rounded-xl border border-gray-100" data-id="element-3730">
                    <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" data-id="element-3731" />
                    <p className="text-sm font-medium text-gray-900" data-id="element-3732">
                      You're offering all suggested services!
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-3733">
                      Add more truck types to get new suggestions.
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Chat Drawer */}
        <AnimatePresence data-id="element-3734">
          {chatOpen && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setChatOpen(false)} className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-40" data-id="element-3735" />
              <motion.div initial={{
            x: '100%'
          }} animate={{
            x: 0
          }} exit={{
            x: '100%'
          }} transition={{
            type: 'spring',
            damping: 30,
            stiffness: 300
          }} className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col" data-id="element-3736">
                <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50 flex items-center justify-between gap-3" data-id="element-3737">
                  <div className="flex items-center gap-3" data-id="element-3738">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center" data-id="element-3739">
                      <Wand2 className="w-4 h-4 text-white" data-id="element-3740" />
                    </div>
                    <div data-id="element-3741">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-3742">
                        Consult with AI
                      </h3>
                      <p className="text-xs text-gray-500" data-id="element-3743">
                        Ask about market trends, pricing, or fleet expansion
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setChatOpen(false)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-white/60 rounded-lg transition-colors" data-id="element-3744">
                    <X className="w-5 h-5" data-id="element-3745" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-3746">
                  {chatMsgs.map(msg => <div key={msg.id} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-3747">
                      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${msg.from === 'user' ? 'bg-gray-900 text-white rounded-br-sm' : 'bg-gray-100 text-gray-900 rounded-bl-sm'}`} data-id="element-3748">
                        {msg.text}
                      </div>
                    </div>)}
                  <div ref={chatEndRef} data-id="element-3749" />
                </div>
                <div className="p-4 border-t border-gray-100 bg-white" data-id="element-3750">
                  <div className="relative" data-id="element-3751">
                    <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') handleSendChat();
                }} placeholder="Ask a question..." autoFocus className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-gray-900 focus:border-gray-900" data-id="element-3752" />
                    <button onClick={handleSendChat} disabled={!chatInput.trim()} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-gray-900 text-white rounded-lg disabled:opacity-50 hover:bg-gray-800 transition-colors" data-id="element-3753">
                      <Send className="w-4 h-4" data-id="element-3754" />
                    </button>
                  </div>
                </div>
              </motion.div>
            </>}
        </AnimatePresence>
      </div>;
  }
  function PricingTab() {
    const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<any>(null);
    const [activeSubTab, setActiveSubTab] = useState<'groups' | 'assignment'>('groups');
    const handleEditGroup = (group: any) => {
      setEditingGroup(group);
      setIsPricingModalOpen(true);
    };
    const assignedCount = services.filter(s => serviceGroupMap[s]).length;
    return <div className="space-y-6" data-id="element-3755">
        {/* Sub-tabs */}
        <div className="bg-white rounded-2xl border border-gray-200 p-1.5 inline-flex gap-1" data-id="element-3756">
          <button onClick={() => setActiveSubTab('groups')} className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeSubTab === 'groups' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-3757">
            <Tag className="w-4 h-4" data-id="element-3758" />
            Pricing Groups
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${activeSubTab === 'groups' ? 'bg-white/15 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-3759">
              {pricingGroups.length}
            </span>
          </button>
          <button onClick={() => setActiveSubTab('assignment')} className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeSubTab === 'assignment' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-3760">
            <Settings2 className="w-4 h-4" data-id="element-3761" />
            Service Assignment
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${activeSubTab === 'assignment' ? 'bg-white/15 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-3762">
              {assignedCount}/{services.length}
            </span>
          </button>
        </div>

        {activeSubTab === 'groups' && <div className="bg-white rounded-2xl border border-gray-200 p-6" data-id="element-3763">
            <div className="flex items-center justify-between mb-6" data-id="element-3764">
              <div data-id="element-3765">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-3766">
                  <Tag className="w-5 h-5 text-blue-600" data-id="element-3767" />
                  Pricing Groups
                </h2>
                <p className="text-sm text-gray-500" data-id="element-3768">
                  Create reusable pricing rules — distance-based tiers, flat
                  fees, or city-pair routes.
                </p>
              </div>
              <button onClick={() => {
            setEditingGroup(null);
            setIsPricingModalOpen(true);
          }} className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition-colors" data-id="element-3769">
                <Plus className="w-4 h-4" data-id="element-3770" />
                Create Group
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3771">
              {pricingGroups.map(group => {
            const usedByCount = services.filter(s => serviceGroupMap[s] === group.id).length;
            return <div key={group.id} className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors" data-id="element-3772">
                    <div className="flex items-start justify-between mb-2" data-id="element-3773">
                      <h3 className="font-bold text-gray-900" data-id="element-3774">{group.name}</h3>
                      <div className="flex items-center gap-1" data-id="element-3775">
                        <button onClick={() => handleEditGroup(group)} className="p-1 text-gray-400 hover:text-gray-900 transition-colors" data-id="element-3776">
                          <Edit2 className="w-4 h-4" data-id="element-3777" />
                        </button>
                        <button onClick={() => deletePricingGroup(group.id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors" data-id="element-3778">
                          <Trash2 className="w-4 h-4" data-id="element-3779" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 mb-3" data-id="element-3780">
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium uppercase tracking-wider" data-id="element-3781">
                        {group.type}
                      </span>
                      {usedByCount > 0 && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium" data-id="element-3782">
                          Used by {usedByCount}
                        </span>}
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-3783">
                      {group.type === 'distance' && <p data-id="element-3784">
                          Base: AED {group.baseFare} • {group.tiers?.length}{' '}
                          tiers
                        </p>}
                      {group.type === 'fixed' && <p data-id="element-3785">Flat Rate: AED {group.flatPrice}</p>}
                      {group.type === 'city' && <p data-id="element-3786">{group.cityPairs?.length} routes defined</p>}
                    </div>
                  </div>;
          })}
              {pricingGroups.length === 0 && <div className="col-span-full p-8 text-center border-2 border-dashed border-gray-200 rounded-xl" data-id="element-3787">
                  <Tag className="w-8 h-8 text-gray-300 mx-auto mb-2" data-id="element-3788" />
                  <p className="text-sm font-medium text-gray-900" data-id="element-3789">
                    No pricing groups yet
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-3790">
                    Create a group to set up automated pricing.
                  </p>
                </div>}
            </div>
          </div>}

        {activeSubTab === 'assignment' && <div className="bg-white rounded-2xl border border-gray-200 p-6" data-id="element-3791">
            <div className="mb-6" data-id="element-3792">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-3793">
                <Settings2 className="w-5 h-5 text-blue-600" data-id="element-3794" />
                Service Assignment
              </h2>
              <p className="text-sm text-gray-500" data-id="element-3795">
                Assign pricing groups to your offered services.{' '}
                {pricingGroups.length === 0 && <button onClick={() => setActiveSubTab('groups')} className="text-blue-600 hover:underline font-medium" data-id="element-3796">
                    Create a pricing group first →
                  </button>}
              </p>
            </div>

            <div className="space-y-2" data-id="element-3797">
              {services.map(service => {
            const assignedGroup = pricingGroups.find(g => g.id === serviceGroupMap[service]);
            return <div key={service} className="flex items-center justify-between gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors" data-id="element-3798">
                    <div className="min-w-0 flex-1" data-id="element-3799">
                      <p className="font-medium text-gray-900 truncate" data-id="element-3800">
                        {service}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-3801">
                        {assignedGroup ? <>
                            Auto-priced via{' '}
                            <span className="font-semibold text-gray-700" data-id="element-3802">
                              {assignedGroup.name}
                            </span>
                          </> : <span className="italic" data-id="element-3803">Manual quote</span>}
                      </p>
                    </div>
                    <select value={serviceGroupMap[service] || ''} onChange={e => assignServiceGroup(service, e.target.value || null)} className="block w-56 sm:w-64 pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-gray-900 focus:border-gray-900 rounded-lg shrink-0" data-id="element-3804">
                      <option value="" data-id="element-3805">
                        No automated pricing (Manual quote)
                      </option>
                      {pricingGroups.map(g => <option key={g.id} value={g.id} data-id="element-3806">
                          {g.name} ({g.type})
                        </option>)}
                    </select>
                  </div>;
          })}
              {services.length === 0 && <p className="text-sm text-gray-500 text-center py-8" data-id="element-3807">
                  Add services in the AI Advisor tab first.
                </p>}
            </div>
          </div>}

        <AnimatePresence data-id="element-3808">
          {isPricingModalOpen && <PricingGroupModal initialData={editingGroup} onClose={() => setIsPricingModalOpen(false)} onSave={data => {
          if (editingGroup) {
            updatePricingGroup(editingGroup.id, data);
          } else {
            addPricingGroup({
              ...data,
              id: `pg-${Date.now()}`
            } as any);
          }
          setIsPricingModalOpen(false);
        }} data-id="element-3809" />}
        </AnimatePresence>
      </div>;
  }
  function PricingGroupModal({
    initialData,
    onClose,
    onSave
  }: any) {
    const [step, setStep] = useState(initialData ? 2 : 1);
    const [name, setName] = useState(initialData?.name || '');
    const [type, setType] = useState<any>(initialData?.type || 'distance');
    // Distance state
    const [baseFare, setBaseFare] = useState(initialData?.baseFare || 100);
    const [tiers, setTiers] = useState<any[]>(initialData?.tiers || [{
      id: '1',
      fromKm: 0,
      toKm: 10,
      price: 50
    }]);
    // Fixed state
    const [flatPrice, setFlatPrice] = useState(initialData?.flatPrice || 500);
    // City state
    const [cityPairs, setCityPairs] = useState<any[]>(initialData?.cityPairs || [{
      id: '1',
      origin: 'Dubai',
      destination: 'Abu Dhabi',
      price: 800,
      bothWays: true
    }]);
    const handleSave = () => {
      if (!name.trim()) {
        toast.error('Please enter a name for the pricing group');
        return;
      }
      const data: any = {
        name,
        type
      };
      if (type === 'distance') {
        data.baseFare = baseFare;
        data.tiers = tiers;
      } else if (type === 'fixed') {
        data.flatPrice = flatPrice;
      } else if (type === 'city') {
        data.cityPairs = cityPairs;
      }
      onSave(data);
    };
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-3810">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-3811">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0" data-id="element-3812">
            <div data-id="element-3813">
              <h3 className="text-lg font-bold text-gray-900" data-id="element-3814">
                {initialData ? 'Edit Pricing Group' : 'Create Pricing Group'}
              </h3>
              <p className="text-sm text-gray-500" data-id="element-3815">
                Step {step} of 2: {step === 1 ? 'Basic Info' : 'Pricing Rules'}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-3816">
              <X className="w-5 h-5" data-id="element-3817" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1" data-id="element-3818">
            {step === 1 && <div className="space-y-6" data-id="element-3819">
                <div data-id="element-3820">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3821">
                    Group Name
                  </label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Standard Local Move" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3822" />
                </div>
                <div data-id="element-3823">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-3824">
                    Pricing Model
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3825">
                    <button onClick={() => setType('distance')} className={`p-4 rounded-xl border-2 text-left transition-colors ${type === 'distance' ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3826">
                      <Route className={`w-6 h-6 mb-2 ${type === 'distance' ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-3827" />
                      <h4 className="font-bold text-gray-900 text-sm" data-id="element-3828">
                        Distance Tiers
                      </h4>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-3829">
                        Base fare + price per km slab
                      </p>
                    </button>
                    <button onClick={() => setType('fixed')} className={`p-4 rounded-xl border-2 text-left transition-colors ${type === 'fixed' ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3830">
                      <Tag className={`w-6 h-6 mb-2 ${type === 'fixed' ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-3831" />
                      <h4 className="font-bold text-gray-900 text-sm" data-id="element-3832">
                        Fixed Price
                      </h4>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-3833">
                        Flat rate regardless of distance
                      </p>
                    </button>
                    <button onClick={() => setType('city')} className={`p-4 rounded-xl border-2 text-left transition-colors ${type === 'city' ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3834">
                      <MapPinned className={`w-6 h-6 mb-2 ${type === 'city' ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-3835" />
                      <h4 className="font-bold text-gray-900 text-sm" data-id="element-3836">
                        City-to-City
                      </h4>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-3837">
                        Specific rates between emirates
                      </p>
                    </button>
                  </div>
                </div>
              </div>}

            {step === 2 && <div className="space-y-6" data-id="element-3838">
                {type === 'distance' && <>
                    <div data-id="element-3839">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3840">
                        Base Fare (AED)
                      </label>
                      <input type="number" value={baseFare} onChange={e => setBaseFare(Number(e.target.value))} className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3841" />
                      <p className="text-xs text-gray-500 mt-1" data-id="element-3842">
                        Applied before any distance calculations.
                      </p>
                    </div>
                    <div data-id="element-3843">
                      <div className="flex items-center justify-between mb-3" data-id="element-3844">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-3845">
                          Distance Tiers
                        </label>
                        <button onClick={() => setTiers([...tiers, {
                    id: Date.now().toString(),
                    fromKm: tiers.length > 0 ? tiers[tiers.length - 1].toKm || 0 : 0,
                    toKm: null,
                    price: 0
                  }])} className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-3846">
                          <Plus className="w-4 h-4" data-id="element-3847" /> Add Tier
                        </button>
                      </div>
                      <div className="space-y-3" data-id="element-3848">
                        {tiers.map((tier, idx) => <div key={tier.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100" data-id="element-3849">
                            <div className="flex-1 grid grid-cols-3 gap-3" data-id="element-3850">
                              <div data-id="element-3851">
                                <span className="text-xs text-gray-500 block mb-1" data-id="element-3852">
                                  From (Km)
                                </span>
                                <input type="number" value={tier.fromKm} onChange={e => {
                          const newTiers = [...tiers];
                          newTiers[idx].fromKm = Number(e.target.value);
                          setTiers(newTiers);
                        }} className="block w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-id="element-3853" />
                              </div>
                              <div data-id="element-3854">
                                <span className="text-xs text-gray-500 block mb-1" data-id="element-3855">
                                  To (Km)
                                </span>
                                <input type="number" value={tier.toKm || ''} placeholder="Max" onChange={e => {
                          const newTiers = [...tiers];
                          newTiers[idx].toKm = e.target.value ? Number(e.target.value) : null;
                          setTiers(newTiers);
                        }} className="block w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-id="element-3856" />
                              </div>
                              <div data-id="element-3857">
                                <span className="text-xs text-gray-500 block mb-1" data-id="element-3858">
                                  Price (AED)
                                </span>
                                <input type="number" value={tier.price} onChange={e => {
                          const newTiers = [...tiers];
                          newTiers[idx].price = Number(e.target.value);
                          setTiers(newTiers);
                        }} className="block w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-id="element-3859" />
                              </div>
                            </div>
                            <button onClick={() => setTiers(tiers.filter(t => t.id !== tier.id))} className="p-2 text-gray-400 hover:text-red-500 mt-5" data-id="element-3860">
                              <Trash2 className="w-4 h-4" data-id="element-3861" />
                            </button>
                          </div>)}
                      </div>
                    </div>
                  </>}

                {type === 'fixed' && <div data-id="element-3862">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3863">
                      Flat Price (AED)
                    </label>
                    <input type="number" value={flatPrice} onChange={e => setFlatPrice(Number(e.target.value))} className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3864" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-3865">
                      This price will be quoted regardless of distance or time.
                    </p>
                  </div>}

                {type === 'city' && <div data-id="element-3866">
                    <div className="flex items-center justify-between mb-3" data-id="element-3867">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-3868">
                        Defined Routes
                      </label>
                      <button onClick={() => setCityPairs([...cityPairs, {
                  id: Date.now().toString(),
                  origin: UAE_CITIES[0],
                  destination: UAE_CITIES[1],
                  price: 0,
                  bothWays: true
                }])} className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-3869">
                        <Plus className="w-4 h-4" data-id="element-3870" /> Add Route
                      </button>
                    </div>
                    <div className="space-y-3" data-id="element-3871">
                      {cityPairs.map((pair, idx) => <div key={pair.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100" data-id="element-3872">
                          <div className="flex items-start gap-3" data-id="element-3873">
                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3874">
                              <div data-id="element-3875">
                                <span className="text-xs text-gray-500 block mb-1" data-id="element-3876">
                                  Origin
                                </span>
                                <select value={pair.origin} onChange={e => {
                          const newPairs = [...cityPairs];
                          newPairs[idx].origin = e.target.value;
                          setCityPairs(newPairs);
                        }} className="block w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-id="element-3877">
                                  {UAE_CITIES.map(city => <option key={city} value={city} data-id="element-3878">
                                      {city}
                                    </option>)}
                                </select>
                              </div>
                              <div data-id="element-3879">
                                <span className="text-xs text-gray-500 block mb-1" data-id="element-3880">
                                  Destination
                                </span>
                                <select value={pair.destination} onChange={e => {
                          const newPairs = [...cityPairs];
                          newPairs[idx].destination = e.target.value;
                          setCityPairs(newPairs);
                        }} className="block w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-id="element-3881">
                                  {UAE_CITIES.map(city => <option key={city} value={city} data-id="element-3882">
                                      {city}
                                    </option>)}
                                </select>
                              </div>
                              <div data-id="element-3883">
                                <span className="text-xs text-gray-500 block mb-1" data-id="element-3884">
                                  Price (AED)
                                </span>
                                <input type="number" value={pair.price} onChange={e => {
                          const newPairs = [...cityPairs];
                          newPairs[idx].price = Number(e.target.value);
                          setCityPairs(newPairs);
                        }} className="block w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-id="element-3885" />
                              </div>
                              <div className="flex items-center mt-5" data-id="element-3886">
                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-id="element-3887">
                                  <input type="checkbox" checked={pair.bothWays} onChange={e => {
                            const newPairs = [...cityPairs];
                            newPairs[idx].bothWays = e.target.checked;
                            setCityPairs(newPairs);
                          }} className="rounded border-gray-300 text-gray-900 focus:ring-gray-900" data-id="element-3888" />
                                  Applies both ways
                                </label>
                              </div>
                            </div>
                            <button onClick={() => setCityPairs(cityPairs.filter(p => p.id !== pair.id))} className="p-2 text-gray-400 hover:text-red-500 mt-5 shrink-0" data-id="element-3889">
                              <Trash2 className="w-4 h-4" data-id="element-3890" />
                            </button>
                          </div>
                        </div>)}
                    </div>
                  </div>}
              </div>}
          </div>

          <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between shrink-0" data-id="element-3891">
            {step === 2 ? <button onClick={() => setStep(1)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 bg-gray-100 rounded-xl transition-colors" data-id="element-3892">
                Back
              </button> : <div data-id="element-3893"></div>}
            <div className="flex gap-3" data-id="element-3894">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-3895">
                Cancel
              </button>
              {step === 1 ? <button onClick={() => {
              if (!name.trim()) {
                toast.error('Please enter a name');
                return;
              }
              setStep(2);
            }} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl transition-colors flex items-center gap-2" data-id="element-3896">
                  Next <ArrowRight className="w-4 h-4" data-id="element-3897" />
                </button> : <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl transition-colors" data-id="element-3898">
                  Save Group
                </button>}
            </div>
          </div>
        </motion.div>
      </div>;
  }
  function UsersTab() {
    const [activeSubTab, setActiveSubTab] = useState<'users' | 'roles' | 'audit'>('users');
    // Mock Data
    const [users, setUsers] = useState([{
      id: '1',
      name: 'Admin User',
      email: 'admin@falcon.ae',
      phone: '+971 50 111 2222',
      role: 'Owner',
      status: 'Active',
      hasPassword: true
    }, {
      id: '2',
      name: 'Sarah Dispatch',
      email: 'sarah@falcon.ae',
      phone: '+971 50 333 4444',
      role: 'Dispatcher',
      status: 'Active',
      hasPassword: true
    }]);
    const [roles, setRoles] = useState([{
      id: 'r1',
      name: 'Owner',
      description: 'Full system access',
      isSystem: true
    }, {
      id: 'r2',
      name: 'Dispatcher',
      description: 'Manage jobs and drivers',
      isSystem: false
    }, {
      id: 'r3',
      name: 'Finance',
      description: 'View payments and invoices',
      isSystem: false
    }]);
    const [auditLogs] = useState([{
      id: 'a1',
      action: 'Invited user Sarah Dispatch',
      user: 'Admin User',
      timestamp: '2 hours ago'
    }, {
      id: 'a2',
      action: 'Changed role for John Doe to Driver Manager',
      user: 'Admin User',
      timestamp: '1 day ago'
    }, {
      id: 'a3',
      action: 'Created role Finance',
      user: 'Admin User',
      timestamp: '2 days ago'
    }]);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [isUserDetailModalOpen, setIsUserDetailModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);
    return <div className="space-y-6" data-id="element-3899">
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-id="element-3900">
          <div className="p-6 border-b border-gray-100" data-id="element-3901">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-3902">
              <Users className="w-5 h-5 text-blue-600" data-id="element-3903" />
              Users & Roles
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-3904">
              Manage team access and permissions
            </p>

            {/* Internal Tabs */}
            <div className="flex gap-2 mt-6" data-id="element-3905">
              <button onClick={() => setActiveSubTab('users')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSubTab === 'users' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3906">
                Users
              </button>
              <button onClick={() => setActiveSubTab('roles')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSubTab === 'roles' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3907">
                Roles
              </button>
              <button onClick={() => setActiveSubTab('audit')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSubTab === 'audit' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3908">
                Audit Log
              </button>
            </div>
          </div>

          <div className="p-0" data-id="element-3909">
            {activeSubTab === 'users' && <div data-id="element-3910">
                <div className="p-4 flex justify-end border-b border-gray-100" data-id="element-3911">
                  <button onClick={() => setIsInviteModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition-colors" data-id="element-3912">
                    <UserPlus className="w-4 h-4" data-id="element-3913" />
                    Invite User
                  </button>
                </div>
                <div className="overflow-x-auto" data-id="element-3914">
                  <table className="w-full text-sm text-left" data-id="element-3915">
                    <thead className="text-xs text-gray-500 bg-gray-50 uppercase" data-id="element-3916">
                      <tr data-id="element-3917">
                        <th className="px-6 py-3" data-id="element-3918">User</th>
                        <th className="px-6 py-3" data-id="element-3919">Role</th>
                        <th className="px-6 py-3" data-id="element-3920">Status</th>
                        <th className="px-6 py-3 text-right" data-id="element-3921">Actions</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-3922">
                      {users.map(u => <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => {
                    setSelectedUser(u);
                    setIsUserDetailModalOpen(true);
                  }} data-id="element-3923">
                          <td className="px-6 py-4" data-id="element-3924">
                            <div className="font-medium text-gray-900" data-id="element-3925">
                              {u.name}
                            </div>
                            <div className="text-gray-500" data-id="element-3926">{u.email}</div>
                          </td>
                          <td className="px-6 py-4" data-id="element-3927">
                            <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium" data-id="element-3928">
                              {u.role}
                            </span>
                          </td>
                          <td className="px-6 py-4" data-id="element-3929">
                            <span className="flex items-center gap-1.5 text-green-600 font-medium" data-id="element-3930">
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-3931"></div>
                              {u.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right" data-id="element-3932">
                            <button className="p-1.5 text-gray-400 hover:text-gray-900 rounded-lg hover:bg-gray-200" onClick={e => {
                        e.stopPropagation();
                        setSelectedUser(u);
                        setIsUserDetailModalOpen(true);
                      }} data-id="element-3933">
                              <MoreVertical className="w-4 h-4" data-id="element-3934" />
                            </button>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>}

            {activeSubTab === 'roles' && <div data-id="element-3935">
                <div className="p-4 flex justify-end border-b border-gray-100" data-id="element-3936">
                  <button onClick={() => setIsCreateRoleModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition-colors" data-id="element-3937">
                    <Plus className="w-4 h-4" data-id="element-3938" />
                    Create Role
                  </button>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3939">
                  {roles.map(r => <div key={r.id} className="border border-gray-200 rounded-xl p-4" data-id="element-3940">
                      <div className="flex justify-between items-start mb-2" data-id="element-3941">
                        <h3 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-3942">
                          {r.name}
                          {r.isSystem && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] uppercase font-bold tracking-wider" data-id="element-3943">
                              System
                            </span>}
                        </h3>
                        {!r.isSystem && <button className="text-gray-400 hover:text-red-500" data-id="element-3944">
                            <Trash2 className="w-4 h-4" data-id="element-3945" />
                          </button>}
                      </div>
                      <p className="text-sm text-gray-500" data-id="element-3946">{r.description}</p>
                    </div>)}
                </div>
              </div>}

            {activeSubTab === 'audit' && <div className="p-0" data-id="element-3947">
                <div className="divide-y divide-gray-100" data-id="element-3948">
                  {auditLogs.map(log => <div key={log.id} className="p-4 flex items-start gap-4 hover:bg-gray-50" data-id="element-3949">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0 mt-1" data-id="element-3950">
                        <FileText className="w-4 h-4 text-gray-500" data-id="element-3951" />
                      </div>
                      <div data-id="element-3952">
                        <p className="text-sm font-medium text-gray-900" data-id="element-3953">
                          {log.action}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-3954">
                          By {log.user} • {log.timestamp}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>

        {/* Modals */}
        <AnimatePresence data-id="element-3955">
          {isInviteModalOpen && <InviteUserModal onClose={() => setIsInviteModalOpen(false)} roles={roles} data-id="element-3956" />}
          {isUserDetailModalOpen && selectedUser && <UserDetailModal user={selectedUser} onClose={() => setIsUserDetailModalOpen(false)} roles={roles} data-id="element-3957" />}
          {isCreateRoleModalOpen && <CreateRoleModal onClose={() => setIsCreateRoleModalOpen(false)} data-id="element-3958" />}
        </AnimatePresence>
      </div>;
  }
  function InviteUserModal({
    onClose,
    roles
  }: any) {
    const [showPassword, setShowPassword] = useState(false);
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-3959">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-3960">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-3961">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-3962">Invite User</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-3963">
              <X className="w-5 h-5" data-id="element-3964" />
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-3965">
            <div data-id="element-3966">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3967">
                Full Name
              </label>
              <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3968" />
            </div>
            <div data-id="element-3969">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3970">
                Email Address
              </label>
              <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3971" />
            </div>
            <div data-id="element-3972">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3973">
                Phone Number
              </label>
              <input type="tel" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3974" />
            </div>
            <div data-id="element-3975">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3976">
                Role
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3977">
                {roles.map((r: any) => <option key={r.id} value={r.id} data-id="element-3978">
                    {r.name}
                  </option>)}
              </select>
            </div>
            <div data-id="element-3979">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3980">
                Initial Password
              </label>
              <div className="relative" data-id="element-3981">
                <input type={showPassword ? 'text' : 'password'} className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3982" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" data-id="element-3983">
                  {showPassword ? <EyeOff className="w-4 h-4" data-id="element-3984" /> : <Eye className="w-4 h-4" data-id="element-3985" />}
                </button>
              </div>
            </div>
          </div>
          <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3" data-id="element-3986">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-3987">
              Cancel
            </button>
            <button onClick={() => {
            toast.success('User invited');
            onClose();
          }} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl" data-id="element-3988">
              Send Invite
            </button>
          </div>
        </motion.div>
      </div>;
  }
  function UserDetailModal({
    user,
    onClose,
    roles
  }: any) {
    const [showPassword, setShowPassword] = useState(false);
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-3989">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-3990">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-3991">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-3992">Edit User</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-3993">
              <X className="w-5 h-5" data-id="element-3994" />
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-3995">
            <div data-id="element-3996">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3997">
                Full Name
              </label>
              <input type="text" defaultValue={user.name} className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-3998" />
            </div>
            <div data-id="element-3999">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4000">
                Email Address
              </label>
              <input type="email" defaultValue={user.email} className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4001" />
            </div>
            <div data-id="element-4002">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4003">
                Phone Number
              </label>
              <input type="tel" defaultValue={user.phone} className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4004" />
            </div>
            <div data-id="element-4005">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4006">
                Role
              </label>
              <select defaultValue={user.role} disabled={user.role === 'Owner'} className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500" data-id="element-4007">
                {roles.map((r: any) => <option key={r.id} value={r.name} data-id="element-4008">
                    {r.name}
                  </option>)}
              </select>
            </div>
            <hr className="border-gray-100 my-4" data-id="element-4009" />
            <div data-id="element-4010">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4011">
                Reset Password
              </label>
              <div className="relative" data-id="element-4012">
                <input type={showPassword ? 'text' : 'password'} placeholder="Enter new password" className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4013" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" data-id="element-4014">
                  {showPassword ? <EyeOff className="w-4 h-4" data-id="element-4015" /> : <Eye className="w-4 h-4" data-id="element-4016" />}
                </button>
              </div>
            </div>
          </div>
          <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between items-center" data-id="element-4017">
            {user.role !== 'Owner' ? <button className="text-sm font-medium text-red-600 hover:text-red-700" data-id="element-4018">
                Remove User
              </button> : <div data-id="element-4019"></div>}
            <div className="flex gap-3" data-id="element-4020">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-4021">
                Cancel
              </button>
              <button onClick={() => {
              toast.success('User updated');
              onClose();
            }} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl" data-id="element-4022">
                Save Changes
              </button>
            </div>
          </div>
        </motion.div>
      </div>;
  }
  function CreateRoleModal({
    onClose
  }: any) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-4023">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-4024">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-4025">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-4026">Create Role</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-4027">
              <X className="w-5 h-5" data-id="element-4028" />
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-4029">
            <div data-id="element-4030">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4031">
                Role Name
              </label>
              <input type="text" placeholder="e.g., Junior Dispatcher" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4032" />
            </div>
            <div data-id="element-4033">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4034">
                Description
              </label>
              <input type="text" placeholder="Brief description of responsibilities" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4035" />
            </div>
            <div className="pt-2" data-id="element-4036">
              <label className="block text-sm font-bold text-gray-900 mb-3" data-id="element-4037">
                Permissions
              </label>
              <div className="space-y-3" data-id="element-4038">
                <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4039">
                  <input type="checkbox" className="rounded border-gray-300 text-gray-900 focus:ring-gray-900" data-id="element-4040" />{' '}
                  Manage Jobs & Dispatch
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4041">
                  <input type="checkbox" className="rounded border-gray-300 text-gray-900 focus:ring-gray-900" data-id="element-4042" />{' '}
                  View Financials
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4043">
                  <input type="checkbox" className="rounded border-gray-300 text-gray-900 focus:ring-gray-900" data-id="element-4044" />{' '}
                  Manage Fleet & Drivers
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-4045">
                  <input type="checkbox" className="rounded border-gray-300 text-gray-900 focus:ring-gray-900" data-id="element-4046" />{' '}
                  Edit Settings
                </label>
              </div>
            </div>
          </div>
          <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3" data-id="element-4047">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-4048">
              Cancel
            </button>
            <button onClick={() => {
            toast.success('Role created');
            onClose();
          }} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl" data-id="element-4049">
              Create Role
            </button>
          </div>
        </motion.div>
      </div>;
  }
  function SecurityTab() {
    return <div className="space-y-6" data-id="element-4050">
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-id="element-4051">
          <div className="mb-6" data-id="element-4052">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-4053">
              <Lock className="w-5 h-5 text-blue-600" data-id="element-4054" />
              Security Settings
            </h2>
            <p className="text-sm text-gray-500" data-id="element-4055">
              Manage your password and account security.
            </p>
          </div>

          <div className="max-w-md space-y-4" data-id="element-4056">
            <div data-id="element-4057">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4058">
                Current Password
              </label>
              <input type="password" placeholder="••••••••" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4059" />
            </div>
            <div data-id="element-4060">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4061">
                New Password
              </label>
              <input type="password" placeholder="••••••••" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4062" />
            </div>
            <div data-id="element-4063">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4064">
                Confirm New Password
              </label>
              <input type="password" placeholder="••••••••" className="block w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-gray-900 focus:border-gray-900 sm:text-sm" data-id="element-4065" />
            </div>

            <div className="pt-4 border-t border-gray-100 mt-6" data-id="element-4066">
              <div className="flex items-center justify-between" data-id="element-4067">
                <div data-id="element-4068">
                  <h4 className="text-sm font-bold text-gray-900" data-id="element-4069">
                    Two-Factor Authentication
                  </h4>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-4070">
                    Add an extra layer of security to your account.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-4071">
                  <input type="checkbox" className="sr-only peer" data-id="element-4072" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-900" data-id="element-4073"></div>
                </label>
              </div>
            </div>

            <div className="pt-6 flex justify-end" data-id="element-4074">
              <button onClick={() => toast.success('Security settings updated')} className="bg-gray-900 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-gray-800 transition-colors" data-id="element-4075">
                Update Security
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
}